import { formatNumber, localDate } from '@/platform/lib/utils';
import {
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
} from '@/platform/ui';
import {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
} from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

export const employmentLine = employment =>
    [
        employment.staff_number && `Pers.nr. ${employment.staff_number}`,
        `In dienst: ${localDate(employment.start_date)}`,
        employment.end_date && `Uit dienst: ${localDate(employment.end_date)}`,
    ]
        .filter(Boolean)
        .join(' · ');

const groupByYear = periods =>
    Object.entries(
        [...periods]
            .sort((a, b) => (a.period?.number ?? 0) - (b.period?.number ?? 0))
            .reduce((years, period) => {
                const year = period.period?.year ?? 'Onbekend';

                (years[year] ??= []).push(period);

                return years;
            }, {})
    );

const PeriodTable = ({ periods, hoursKey, hoursLabel }) => (
    <Table striped>
        <TableHeader>
            <TableRow>
                <TableHeaderCell>Periode</TableHeaderCell>
                <TableHeaderCell>Aard arbeidsverhouding</TableHeaderCell>
                <TableHeaderCell textAlign='right'>
                    {hoursLabel}
                </TableHeaderCell>
            </TableRow>
        </TableHeader>
        <TableBody>
            {periods.map(period => (
                <TableRow key={period.id}>
                    <TableCell>{period.period?.number}</TableCell>
                    <TableCell>{period.nature_of_employment}</TableCell>
                    <TableCell textAlign='right'>
                        {formatNumber(period[hoursKey])}
                    </TableCell>
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

const EmploymentPeriods = ({ employment, periods, hoursKey, hoursLabel }) => {
    const years = groupByYear(periods ?? []);

    return (
        <Disclosure as='div' className='rounded-lg border border-gray-200'>
            <DisclosureButton className='group focus-visible:outline-primary-600 flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2'>
                {employmentLine(employment)}
                <ChevronDownIcon
                    aria-hidden='true'
                    className='size-5 shrink-0 text-gray-400 transition-transform duration-200 group-hover:text-gray-600 group-data-open:rotate-180'
                />
            </DisclosureButton>
            <DisclosurePanel className='border-t border-gray-200 px-4 pb-4'>
                {!years.length && (
                    <p className='pt-4 text-sm text-gray-500'>
                        Geen periodegegevens
                    </p>
                )}

                {years.length > 0 && (
                    <Tabs defaultIndex={years.length - 1}>
                        <TabList>
                            {years.map(([year]) => (
                                <Tab key={year}>{year}</Tab>
                            ))}
                        </TabList>
                        <TabPanels className='mt-4'>
                            {years.map(([year, yearPeriods]) => (
                                <TabPanel key={year}>
                                    <PeriodTable
                                        periods={yearPeriods}
                                        hoursKey={hoursKey}
                                        hoursLabel={hoursLabel}
                                    />
                                </TabPanel>
                            ))}
                        </TabPanels>
                    </Tabs>
                )}
            </DisclosurePanel>
        </Disclosure>
    );
};

export default EmploymentPeriods;
